import React, { FC, ReactNode, useEffect, useState, useCallback, useMemo } from 'react';
import "./AnimatedSearch.css"
import { CSSTransition } from "react-transition-group";
import { ReactComponent as Search } from "../../../assets/search_back.svg"
import Button from "../Button/Button";
import useOutsideAlerter from "../../../hooks/useOutsideAlert";

export interface IAnimatedSearch {
    widthInput: number;
    value: string;
    placeholder?: string;
    children?: ReactNode;
    onSubmitFunc?: (value: string) => void;
    changeSearchValue?: (value: string) => void;
    hideSubmitButton?: boolean;
    showPreviewList?: boolean;
    swapFuncButton?: boolean;
    stashDate?: boolean;
    callBackStash?: (stash: boolean) => void;
}

const AnimatedSearch: FC<IAnimatedSearch> = ({
    value,
    placeholder,
    children,
    onSubmitFunc,
    changeSearchValue,
    hideSubmitButton,
    showPreviewList,
    widthInput,
    swapFuncButton,
    stashDate,
    callBackStash
}) => {
    const [isMoved, setIsMoved] = useState(false);
    const [buttonSwap, setButtonSwap] = useState(false);
    
    const resetState = useCallback(() => {
        setIsMoved(false);
        changeSearchValue?.("");
        setButtonSwap(false);
        callBackStash?.(true);
    }, [changeSearchValue, callBackStash]);

    const outsideAlerterRef = useOutsideAlerter<HTMLDivElement>(resetState);

    useEffect(() => {
        if (stashDate) {
            resetState();
        }
    }, [stashDate, resetState]);

    const funcButtonSwap = useCallback(() => {
        setIsMoved(false);
        setButtonSwap(false);
        onSubmitFunc?.(value);
        changeSearchValue?.("");
    }, [value, onSubmitFunc, changeSearchValue]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            funcButtonSwap();
        }
    }, [funcButtonSwap]);

    const startAnimation = useCallback(() => {
        if (swapFuncButton) {
            setButtonSwap(true);
        }
        setIsMoved(prevState => !prevState);
    }, [swapFuncButton]);

    const handleButtonClick = useCallback(() => {
        buttonSwap ? funcButtonSwap() : startAnimation();
    }, [buttonSwap, funcButtonSwap, startAnimation]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        changeSearchValue?.(e.target.value);
    }, [changeSearchValue]);

    const inputStyle = useMemo(() => ({
        width: isMoved ? `${widthInput}px` : "0px",
        padding: isMoved ? undefined : "0px"
    }), [isMoved, widthInput]);

    const backgroundStyle = useMemo(() => ({
        borderRadius: showPreviewList && isMoved ? "6px 6px 0px 0px" : undefined
    }), [showPreviewList, isMoved]);

    const childStyle = useMemo(() => ({
        width: isMoved ? `${widthInput + 36}px` : "0px",
        borderRadius: "0px 0px 6px 6px"
    }), [isMoved, widthInput]);

    const posClassName = `ui__component--animated__search--body ${isMoved ? 'moved' : ''}`;
    const backgroundClassName = isMoved 
        ? "ui__component--animated__search--pos__input" 
        : "ui__component--animated__search--pos__input--non__color";

    return (
        <div ref={outsideAlerterRef} className="ui__component--animated__search">
            <div className="ui__component--animated__search--abs__pos">
                <div className="ui__component--animated__search--pos">
                    <div
                        className={backgroundClassName}
                        style={backgroundStyle}
                    >
                        <input
                            placeholder={placeholder}
                            style={inputStyle}
                            className={posClassName}
                            value={value}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                        />
                        {!hideSubmitButton && (
                            <Button 
                                callBack={handleButtonClick} 
                                image={<Search/>} 
                                buttonSize='small'
                                className="ui__component--animated__search--button"
                            />
                        )}
                    </div>
                    {showPreviewList && isMoved && (
                        <div className="ui__component--animated__search--child">
                            <div
                                className="ui__component--animated__search--pos__child"
                                style={childStyle}
                            >
                                {children}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnimatedSearch;